/**
 * SettingsRepository - User preferences repository
 * Handles user settings persistence and loading
 */

import { UserSettings } from '../types/GameTypes';
import { StorageService } from '../services/StorageService';
import { DEFAULT_WORD_PACK } from '../data/wordpacks';
import { config } from '../config';

export class SettingsRepository {
  private storageService: StorageService;
  
  constructor(storageService: StorageService) {
    this.storageService = storageService;
  }

  /**
   * Load user settings or return defaults
   * Pure read operation - no migration logic
   */
  loadUserSettings(): UserSettings {
    const saved = this.storageService.get<UserSettings>(config.storageKeys.userSettings);
    if (saved) {
      // Merge with defaults to handle partial settings
      const defaults = this.getDefaultSettings();
      return { ...defaults, ...saved };
    }

    return this.getDefaultSettings();
  }

  /**
   * Get default settings (updated for wordpack system)
   */
  private getDefaultSettings(): UserSettings {
    return {
      darkMode: true, // Default to dark mode (matches original)
      rstlneMode: true, // Default to RSTLNE mode enabled
      selectedWordPack: DEFAULT_WORD_PACK, // Default to 'all-words' pack
      showGuessedLetters: false, // FIXED: Default to false (matches original)
      animationsEnabled: true // Default to enabled
    };
  }

  /**
   * Save user settings
   */
  saveUserSettings(settings: UserSettings): void {
    this.storageService.set(config.storageKeys.userSettings, settings);
  }

  /**
   * Update a single setting
   */
  updateSetting<K extends keyof UserSettings>(key: K, value: UserSettings[K]): void {
    const currentSettings = this.loadUserSettings();
    const updatedSettings = {
      ...currentSettings,
      [key]: value
    };
    this.saveUserSettings(updatedSettings);
  }

  /**
   * Reset settings to defaults
   */
  resetToDefaults(): void {
    this.storageService.remove(config.storageKeys.userSettings);
  }

  /**
   * Check if a setting has been customized from default
   */
  isSettingCustomized(key: keyof UserSettings): boolean {
    const currentSettings = this.loadUserSettings();
    const defaultSettings = this.getDefaultSettings();
    
    return currentSettings[key] !== defaultSettings[key];
  }
}
